import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { apiService } from '../Config/api.service';

const CustomTooltip = ({ active, payload, label, dateLocale }) => {
    if (active && payload && payload.length) {
        return (
            <div className="p-4 bg-white rounded-lg shadow-lg border border-gray-200">
                <p className="font-bold text-gray-700">{label}</p>
                {payload.map((pld, index) => (
                    <p key={index} style={{ color: pld.color }}>
                        {`${pld.name}: ${Number(pld.value).toLocaleString(dateLocale)} CFA`}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

const SalaryEvolutionChart = ({ employeeId }) => {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'fr-FR';
    const [data, setData] = useState([]);
    const [groupBy, setGroupBy] = useState('monthly'); // 'monthly' or 'annual'
    
    // Default date range: last 12 months
    const today = new Date();
    const lastYear = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    
    const [startDate, setStartDate] = useState(lastYear.toISOString().slice(0, 7));
    const [endDate, setEndDate] = useState(today.toISOString().slice(0, 7));
    
    const [startYear, setStartYear] = useState(today.getFullYear() - 5);
    const [endYear, setEndYear] = useState(today.getFullYear());

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!employeeId) return;

        const fetchSalaryData = async () => {
            setLoading(true);
            setError(null);
            
            const params = new URLSearchParams();
            params.append('group_by', groupBy);

            if (groupBy === 'monthly') {
                params.append('start_date', `${startDate}-01`);
                // Correction: Pour inclure le mois de fin, il suffit d'envoyer
                // une date de fin qui tombe dans ce mois, comme le premier jour.
                // Le backend se charge du reste.
                params.append('end_date', `${endDate}-01`);
            } else { // annual
                params.append('start_date', `${startYear}-01-01`);
                params.append('end_date', `${endYear}-12-31`);
            }

            try {
                const response = await apiService.getElement(`/employee/salary_evolution/${employeeId}?${params.toString()}`);
                setData(response.data.evolution);
            } catch (err) {
                setError(t('salaryEvolutionChart.fetchError'));
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchSalaryData();
    }, [employeeId, groupBy, startDate, endDate, startYear, endYear]);

    const formatYAxis = (tickItem) => `${Math.round(tickItem / 1000)}k`;

    const renderDateFilters = () => {
        if (groupBy === 'monthly') {
            return (
                <>
                    <input type="month" value={startDate} onChange={e => setStartDate(e.target.value)} className="input input-bordered input-sm" />
                    <span className="mx-2">{t('salaryEvolutionChart.to')}</span>
                    <input type="month" value={endDate} onChange={e => setEndDate(e.target.value)} className="input input-bordered input-sm" />
                </>
            );
        }
        // annual
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 20 }, (_, i) => currentYear - i);
        return (
            <>
                <select value={startYear} onChange={e => setStartYear(e.target.value)} className="select select-bordered select-sm">
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <span className="mx-2">{t('salaryEvolutionChart.to')}</span>
                <select value={endYear} onChange={e => setEndYear(e.target.value)} className="select select-bordered select-sm">
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
            </>
        );
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h3 className="text-xl font-bold text-gray-800">{t('salaryEvolutionChart.title')}</h3>
                <div className="flex items-center gap-4">
                    <select
                        value={groupBy}
                        onChange={(e) => setGroupBy(e.target.value)}
                        className="select select-bordered select-sm"
                    >
                        <option value="monthly">{t('salaryEvolutionChart.monthlyView')}</option>
                        <option value="annual">{t('salaryEvolutionChart.annualView')}</option>
                    </select>
                    {renderDateFilters()}
                </div>
            </div>

            {loading ? (
                <div className="h-80 flex items-center justify-center">
                    <p>{t('dailySalaryInputs.loadingData')}</p>
                </div>
            ) : error ? (
                <div className="h-80 flex items-center justify-center text-red-500">
                    <p>{error}</p>
                </div>
            ) : data.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-gray-500">
                    <p>{t('salaryEvolutionChart.noDataAvailable')}</p>
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                        data={data}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="date" stroke="#6b7280" />
                        <YAxis stroke="#6b7280" tickFormatter={formatYAxis} />
                        <Tooltip content={<CustomTooltip dateLocale={dateLocale} />} />
                        <Legend />
                        <Line type="monotone" dataKey="salaire" name={t('payslipsList.grossSalary')} stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                        <Line type="monotone" dataKey="net" name={t('payslipsList.netSalary')} stroke="#8b5cf6" strokeWidth={2} />
                        <Line type="monotone" dataKey="charges" name={t('salaryEvolutionChart.employeeCharges')} stroke="#ef4444" strokeWidth={2} />
                        <Line type="monotone" dataKey="cotisations" name={t('salaryEvolutionChart.employerContributions')} stroke="#22c55e" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default SalaryEvolutionChart;